import {
  DeleteFilled,
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Menu, MenuProps, Modal } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { type FileCard as FileCardType } from 'services';
import styles from '../FilePicker.module.scss';

type CardMenuProps = {
  file: FileCardType;
  onDelete?: (file: FileCardType[]) => void;
  onDownload?: (file: FileCardType) => void;
  onDetails?: (file: FileCardType) => void;
  onShare?: (file: FileCardType) => void;
};

export const CardMenu: React.FC<CardMenuProps> = ({ onDelete, file, onDownload, onDetails, onShare }) => {
  const confirmDelete = useCallback(() => {
    Modal.confirm({
      title: 'Are you sure to delete this file?',
      className: styles.modalDelete,
      icon: <DeleteFilled className={styles.modalDeleteIcon} />,
      centered: true,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        if (onDelete) onDelete([file]);
      },
    });
  }, [file, onDelete]);

  const showDivider = (!!onDownload || !!onShare || !!onDetails) && !!onDelete;

  const menuItems = useMemo((): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (onDownload) {
      items.push({
        key: 'download',
        icon: <DownloadOutlined />,
        onClick: () => {
          onDownload(file);
        },
        label: 'Download',
      });
    }

    if (onShare) {
      items.push({
        key: 'share',
        icon: <ShareAltOutlined />,
        onClick: () => {
          onShare(file);
        },
        label: 'Share',
      });
    }

    if (onDetails) {
      items.push({
        key: 'details',
        icon: <InfoCircleOutlined />,
        onClick: () => {
          onDetails(file);
        },
        label: 'Details',
      });
    }

    if (showDivider) {
      items.push({
        type: 'divider',
      });
    }

    if (onDelete) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        onClick: confirmDelete,
        label: 'Delete',
      });
    }

    return items;
  }, [file, onDownload, confirmDelete, onDelete, onDetails, onShare, showDivider]);

  return (
    <Dropdown
      className={styles.moreMenuToggle}
      overlay={<Menu items={menuItems} />}
      placement="bottomRight"
      openClassName={styles.moreMenuToggleOpen}
    >
      <Button shape="circle" size="small" icon={<MoreOutlined />} />
    </Dropdown>
  );
};
